/**
 * Phase 10 R4 — computeTicketFinancialSummary
 *
 * Pure derive helper. Computes ticket-level Gross Profit (GP) from already-
 * derived inputs:
 *   - Parts sell  ← computePartLineEconomics(line).lineSubtotal
 *   - Parts cost  ← Σ (qty * unit_cost) skipping null cost
 *   - Labor sell  ← R2 useTicketLaborLines totals.total_labor_sell
 *   - Labor cost  ← R3 useTicketTechPayLines totals.total_tech_pay_amount
 *                   (already 'computed'-only by R3 contract)
 *
 * STRICT BOUNDARIES:
 *   - GP ≠ Net profit. Excludes taxes, fees, core charges, environmental fees,
 *     overhead, payroll taxes, merchant fees.
 *   - Never fabricates a labor cost number for deferred_hourly / no_config /
 *     uncomputed pay rows. Partial total stands; status is surfaced as
 *     'partial' or 'insufficient'.
 *   - No persistence. No mutations. Pure function.
 *   - No override layer — GP is fully derived from the underlying sell/cost.
 */
import type { PartsLine } from '@/hooks/parts/usePartsLines';
import {
  computePartLineEconomics,
  type PricingMatrixRow,
} from '@/lib/parts-financial/calculator';
import type { TicketLaborTotals } from '@/hooks/labor/useTicketLaborLines';
import type {
  TicketTechPayLine,
  TicketTechPayTotals,
} from '@/hooks/labor/useTicketTechPayLines';

export type CompletenessStatus = 'complete' | 'partial' | 'insufficient';

export interface FinancialBucket {
  sell: number;
  cost: number;
  gpAmount: number;
  gpPercent: number;
}

export interface TicketFinancialCompleteness {
  laborCostStatus: CompletenessStatus;
  laborSellStatus: CompletenessStatus;
  partsStatus: CompletenessStatus;
  overallStatus: CompletenessStatus;
  deferredHourlyCount: number;
  noConfigCount: number;
  uncomputedPayCount: number;
  uncomputedLaborCount: number;
  partsMissingCostCount: number;
  /** Human-readable warning lines for the UI. */
  notes: string[];
}

export interface TicketFinancialSummary {
  parts: FinancialBucket;
  labor: FinancialBucket;
  combined: FinancialBucket;
  completeness: TicketFinancialCompleteness;
}

export interface ComputeTicketFinancialSummaryInput {
  partsLines: PartsLine[];
  laborTotals: TicketLaborTotals | null | undefined;
  techPayLines: TicketTechPayLine[] | null | undefined;
  techPayTotals: TicketTechPayTotals | null | undefined;
  matrix: PricingMatrixRow[] | null | undefined;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

function bucket(sell: number, cost: number): FinancialBucket {
  const s = r2(sell);
  const c = r2(cost);
  const gp = r2(s - c);
  const pct = s > 0 ? r2(((s - c) / s) * 100) : 0;
  return { sell: s, cost: c, gpAmount: gp, gpPercent: pct };
}

/** Worst-of across statuses: insufficient > partial > complete. */
function worstOf(...xs: CompletenessStatus[]): CompletenessStatus {
  if (xs.includes('insufficient')) return 'insufficient';
  if (xs.includes('partial')) return 'partial';
  return 'complete';
}

export function computeTicketFinancialSummary(
  input: ComputeTicketFinancialSummaryInput,
): TicketFinancialSummary {
  const { partsLines, laborTotals, techPayLines, techPayTotals, matrix } = input;

  // ── Parts ───────────────────────────────────────────────────────────────
  let partsSell = 0;
  let partsCost = 0;
  let partsMissingCostCount = 0;
  let partsLineCount = 0;

  for (const line of partsLines ?? []) {
    if (line.deleted_at) continue;
    partsLineCount += 1;
    const econ = computePartLineEconomics({
      qty: line.quantity,
      unitCost: line.unit_cost,
      coreCharge: line.core_charge,
      taxable: line.taxable,
      matrix: matrix ?? undefined,
      actualSellEach: line.actual_sell_each,
      actualSellTotal: line.actual_sell_total,
      pricingMethod: line.pricing_method,
      manualPriceLocked: line.manual_price_locked,
    });
    partsSell += econ.lineSubtotal;
    if (line.unit_cost == null) {
      partsMissingCostCount += 1;
    } else {
      partsCost += Number(line.quantity || 0) * Number(line.unit_cost);
    }
  }

  const partsStatus: CompletenessStatus =
    partsLineCount === 0
      ? 'complete'
      : partsMissingCostCount === 0
        ? 'complete'
        : partsMissingCostCount === partsLineCount
          ? 'insufficient'
          : 'partial';

  // ── Labor sell (R2) ─────────────────────────────────────────────────────
  const laborSell = laborTotals?.total_labor_sell ?? 0;
  const uncomputedLaborCount = laborTotals?.uncomputed_count ?? 0;
  const computedLaborCount = laborTotals?.computed_count ?? 0;
  const laborSellStatus: CompletenessStatus =
    computedLaborCount + uncomputedLaborCount === 0
      ? 'complete'
      : uncomputedLaborCount === 0
        ? 'complete'
        : computedLaborCount === 0
          ? 'insufficient'
          : 'partial';

  // ── Labor cost (R3 — 'computed'-only by contract) ───────────────────────
  const laborCost = techPayTotals?.total_tech_pay_amount ?? 0;
  const deferredHourlyCount = techPayTotals?.deferred_hourly_count ?? 0;
  const noConfigCount = techPayTotals?.no_config_count ?? 0;
  const uncomputedPayCount = techPayTotals?.uncomputed_count ?? 0;
  const computedPayCount = techPayTotals?.computed_count ?? 0;
  const payLineCount = techPayLines?.length ?? 0;

  let laborCostStatus: CompletenessStatus;
  if (payLineCount === 0) {
    // No pay rows at all → if there is labor sell, cost is insufficient; else complete (nothing to compute).
    laborCostStatus = laborSell > 0 ? 'insufficient' : 'complete';
  } else if (deferredHourlyCount + noConfigCount + uncomputedPayCount === 0) {
    laborCostStatus = 'complete';
  } else if (computedPayCount === 0) {
    laborCostStatus = 'insufficient';
  } else {
    laborCostStatus = 'partial';
  }

  // ── Buckets ─────────────────────────────────────────────────────────────
  const parts = bucket(partsSell, partsCost);
  const labor = bucket(laborSell, laborCost);
  const combined = bucket(parts.sell + labor.sell, parts.cost + labor.cost);

  // ── Notes ───────────────────────────────────────────────────────────────
  const notes: string[] = [];
  if (deferredHourlyCount > 0) {
    notes.push(
      `Labor cost partial: ${deferredHourlyCount} hourly job${
        deferredHourlyCount === 1 ? '' : 's'
      } deferred`,
    );
  }
  if (noConfigCount > 0) {
    notes.push(
      `${noConfigCount} labor row${noConfigCount === 1 ? '' : 's'} missing tech pay config`,
    );
  }
  if (uncomputedPayCount > 0) {
    notes.push(
      `${uncomputedPayCount} labor row${uncomputedPayCount === 1 ? '' : 's'} pay not yet computed`,
    );
  }
  if (uncomputedLaborCount > 0) {
    notes.push(
      `${uncomputedLaborCount} labor row${
        uncomputedLaborCount === 1 ? '' : 's'
      } sell not yet computed`,
    );
  }
  if (partsMissingCostCount > 0) {
    notes.push(
      `${partsMissingCostCount} part${
        partsMissingCostCount === 1 ? '' : 's'
      } missing cost data`,
    );
  }

  const overallStatus = worstOf(partsStatus, laborSellStatus, laborCostStatus);

  return {
    parts,
    labor,
    combined,
    completeness: {
      laborCostStatus,
      laborSellStatus,
      partsStatus,
      overallStatus,
      deferredHourlyCount,
      noConfigCount,
      uncomputedPayCount,
      uncomputedLaborCount,
      partsMissingCostCount,
      notes,
    },
  };
}
